import { User, UserRole } from '../schemas/auth';

// JWT Payload interface
interface JWTPayload {
  exp: number;
  iat?: number;
  sub?: string;
  [key: string]: string | number | boolean | null | undefined;
}

// Token management utilities
class TokenManager {
  private accessTokenKey = 'access_token';
  private refreshTokenKey = 'refresh_token';

  getAccessToken(): string | null {
    return localStorage.getItem(this.accessTokenKey);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.accessTokenKey, accessToken);
    localStorage.setItem(this.refreshTokenKey, refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }

  isTokenExpired(token: string): boolean {
    if (!token) return true;

    try {
      // Decode JWT token (basic implementation)
      const payload = JSON.parse(atob(token.split('.')[1])) as JWTPayload;
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      // If we can't decode the token, assume it's expired
      return true;
    }
  }

  // Get time until token expires in milliseconds
  getTimeUntilExpiry(token: string): number {
    if (!token) return 0;

    try {
      const payload = JSON.parse(atob(token.split('.')[1])) as JWTPayload;
      const currentTime = Date.now() / 1000;
      return Math.max(0, (payload.exp - currentTime) * 1000);
    } catch (error) {
      return 0;
    }
  }
}

// User data management utilities
class UserManager {
  private userKey = 'user_data';

  getUser(): User | null {
    try {
      const userData = localStorage.getItem(this.userKey);
      return userData ? (JSON.parse(userData) as User) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  setUser(user: User): void {
    try {
      localStorage.setItem(this.userKey, JSON.stringify(user));
    } catch (error) {
      console.error('Error storing user data:', error);
    }
  }

  clearUser(): void {
    localStorage.removeItem(this.userKey);
  }

  updateUser(updates: Partial<User>): User | null {
    const currentUser = this.getUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...updates };
      this.setUser(updatedUser);
      return updatedUser;
    }
    return null;
  }

  // Get specific user property
  getUserProperty<K extends keyof User>(property: K): User[K] | null {
    const user = this.getUser();
    return user ? user[property] : null;
  }

  // Check if user has specific role
  hasRole(role: UserRole): boolean {
    const user = this.getUser();
    return user ? user.role === role : false;
  }

  // Check if user has any of the specified roles
  hasAnyRole(roles: UserRole[]): boolean {
    const user = this.getUser();
    if (!user) return false;
    return roles.includes(user.role);
  }
}

// Create singleton instances
export const tokenManager = new TokenManager();
export const userManager = new UserManager();

// Export classes if needed for testing or extending
export { TokenManager, UserManager };

// Helper functions for common operations
export const authUtils = {
  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = tokenManager.getAccessToken();
    const user = userManager.getUser();
    return !!(token && user && !tokenManager.isTokenExpired(token));
  },

  // Get current user with token validation
  getCurrentUser: (): User | null => {
    const token = tokenManager.getAccessToken();
    const user = userManager.getUser();

    if (!token || !user || tokenManager.isTokenExpired(token)) {
      return null;
    }

    return user;
  },

  // Clear all authentication data
  clearAuth: (): void => {
    tokenManager.clearTokens();
    userManager.clearUser();
  },

  // Setup automatic token refresh
  setupTokenRefresh: (
    refreshCallback: () => void,
    bufferTime: number = 5 * 60 * 1000
  ): NodeJS.Timeout | null => {
    const token = tokenManager.getAccessToken();
    if (!token) return null;

    const timeUntilExpiry = tokenManager.getTimeUntilExpiry(token);
    const refreshTime = Math.max(0, timeUntilExpiry - bufferTime);

    if (refreshTime > 0) {
      return setTimeout(refreshCallback, refreshTime);
    }

    return null;
  },
};

export default authUtils;
